<template>
  <scoped>
    <h1>
      Order
    </h1>
    <table v-if="order">
      <tbody>
        <tr>
          <th>
            Total Price
          </th>
          <td>
            <template v-if="order.price">
              {{ order.price.grossTotal.toFixed(2) }}
            </template>
          </td>
        </tr>
        <tr>
          <th>
            Currency
          </th>
          <td>
            {{ order.currency }}
          </td>
        </tr>
        <tr>
          <th>
            Memorandum
          </th>
          <td>
            {{ order.memorandum }}
          </td>
        </tr>
        <tr>
          <th>
            Paid
          </th>
          <td>
            <template v-if="order.paid">
              {{ order.paid.toFixed(2) }}
            </template>
          </td>
        </tr>
        <tr>
          <th>
            Received
          </th>
          <td>
            <template v-if="order.received">
              {{ order.received.toFixed(2) }}
            </template>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-if="order && order.orderLines">
      <h2>
        Order Lines
      </h2>
      <OrderLineList
        v-model:selectedIndex="selectedOrderLineIndex"
        :items="order.orderLines.records"
        readOnly
      />
      <ContentModal v-if="selectedOrderLineIndex != null">
        <OrderLineEditor
          :value="order.orderLines.records[selectedOrderLineIndex]"
          readOnly
        />
        <br>
        <Button
          title="Close"
          @click="selectedOrderLineIndex = null"
        />
      </ContentModal>
    </div>
    <div v-if="order && order.customer && order.relationType == 'CUSTOMER'">
      <Button
        v-if="order.type == 'SALE_DEBIT'"
        title="Send Invoice Mail"
        @click="mailInvoice()"
      />
      <Button
        v-if="order.type == 'SALE_OFFER'"
        title="Send Offer Mail"
        @click="mailOffer()"
      />
      <h2>
        Customer
      </h2>
      <table>
        <tbody>
          <tr>
            <th>
              Type
            </th>
            <td>
              {{ order.customer.type }}
            </td>
          </tr>
          <tr>
            <th>
              Name
            </th>
            <td>
              {{ order.customer.name }}
            </td>
          </tr>
          <tr>
            <th>
              Number
            </th>
            <td>
              {{ order.customer.number }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="order && order.relationType == 'SUPPLIER' && order.supplier">
      <h2>
        Supplier
      </h2>
      <table>
        <tbody>
          <tr>
            <th>
              Name
            </th>
            <td>
              {{ order.supplier.name }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="order && order.files">
      <h2>
        Files
      </h2>
      <FileList
        v-model:selectedIndex="selectedFileIndex"
        :items="order.files.records"
        readOnly
      />
      <ContentModal v-if="selectedFileIndex">
        <FileEditor
          :value="order.files.records[selectedFileIndex]"
          readOnly
        />
        <br>
        <Button
          title="Close"
          @click="selectedFileIndex = null"
        />
      </ContentModal>
      <ErrorModal
        v-if="error"
        v-model:error="error"
      />
    </div>
  </scoped>
</template>

<script setup lang="ts">
  import OrderLineList from  "@/components/lists/order_line.vue"
  import {Order as OrderClient} from "@/endpoints/rutsolve/gql/resolvers/order"
  import {onBeforeMount, ref} from "vue"
  import {Order} from "@/endpoints/rutsolve/gql/generated/graphql"
  import ErrorModal from "@/components/modals/error.vue"
  import Button from "@/components/button.vue"
  import OrderLineEditor from "@/components/editors/order_line.vue"
  import ContentModal from "@/components/modals/content.vue"
  import {Email as EmailClient} from "@/endpoints/rutsolve/rest/email"
  import FileList from "@/components/lists/file.vue"
  import FileEditor from "@/components/editors/file.vue"

  const props = defineProps({
    orderID: {
      default: null,
      required: true,
      type: String
    }
  })

  const error = ref<Error>(null)
  const orderClient = new OrderClient()
  const order = ref<Order>(null)
  const selectedFileIndex = ref<number>(null)
  const selectedOrderLineIndex = ref<number>(null)

  onBeforeMount( () => {
    read()
  })
  
  async function read() {
    try {
      order.value = await orderClient.summary(props.orderID)
    } catch(e) {
      error.value = e
    }
  }

  async function mailInvoice() {
    try {
      await EmailClient.invoice(order.value.id)
    } catch(e) {
      error.value = e
    }
  }

  async function mailOffer() {
    try {
      await EmailClient.offer(order.value.id)
    } catch(e) {
      error.value = e
    }
  }
</script>
